import { Dropdown } from 'tailwindcss-stimulus-components'
import { createPopper, Instance } from '@popperjs/core'

export default class extends Dropdown {
  static targets = ['menu', 'button']
  static values = { open: Boolean, adaptive: Boolean }

  declare menuTarget: HTMLElement
  declare buttonTarget: HTMLElement
  declare hasButtonTarget: boolean
  declare adaptiveValue: boolean
  declare openValue: boolean
  declare popperInstance: Instance | null
  declare popoverOpen: boolean
  declare initialStateApplied: boolean

  initialize (): void {
    this.initialStateApplied = false
    super.initialize()
    this.popperInstance = null
    this.popoverOpen = false
    this.openValue = false
    this.menuTarget.classList.add('hidden')
  }

  disconnect (): void {
    super.disconnect()
    this.closePopover()
    this.destroyPopper()
  }

  closeOnMouseClick ({ x, y }: { x: Number, y: Number }): void {
    // Fix for keyboard focus select
    if (this.openValue && x !== 0 && y !== 0) {
      this.toggle()
    }
  }

  openValueChanged (): void {
    if (!this.initialStateApplied) {
      this.initialStateApplied = true

      if (!this.openValue) {
        this.menuTarget.classList.add('hidden')
        this.syncAriaExpanded()
        return
      }
    }

    super.openValueChanged()
    this.syncAriaExpanded()
  }

  private syncAriaExpanded (): void {
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute('aria-expanded', String(this.openValue))
    }
  }

  toggle (): void {
    super.toggle()
    this.adjustDropdown()
  }

  closeOnSubmit (e: { target: HTMLElement }): void {
    // Fix to prevent opening dropdown after submitting other form
    if (this.openValue && e.target.tagName === 'BUTTON') {
      this.toggle()
    }
  }

  hide (e: { target: HTMLElement }): void {
    // Fix for file input in Trix editor
    if ((e.target.tagName === 'INPUT' && (e.target as HTMLInputElement).type === 'file') || !document.body.contains(e.target)) {
      return
    }

    super.hide(e)
    this.adjustDropdown()
  }

  closeWithTimeout (): void {
    setTimeout(() => {
      super.close()
    }, 100)
  }

  private adjustDropdown (): void {
    if (this.adaptiveValue) {
      if (this.openValue && this.hasButtonTarget) {
        this.update()
      } else if (!this.openValue) {
        this.reset()
      }
    }

    this.rowZIndex()
  }

  private update (): void {
    setTimeout(() => {
      if (!this.openValue || !this.hasButtonTarget) return

      this.openPopover()
      this.destroyPopper()
      this.popperInstance = createPopper(this.buttonTarget, this.menuTarget, {
        placement: 'bottom-end',
        strategy: 'fixed',
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 4]
            }
          },
          {
            name: 'flip',
            options: {
              boundary: 'viewport',
              fallbackPlacements: ['top-end']
            }
          },
          {
            name: 'preventOverflow',
            options: {
              boundary: 'viewport',
              padding: 8
            }
          },
          {
            name: 'computeStyles',
            options: {
              gpuAcceleration: false
            }
          }
        ]
      })
    }, 0)
  }

  private openPopover (): void {
    if (!this.popoverSupported() || this.popoverOpen || !this.menuTarget.isConnected) return

    this.menuTarget.showPopover()
    this.popoverOpen = true
  }

  private closePopover (): void {
    if (!this.popoverSupported() || !this.popoverOpen || !this.menuTarget.isConnected) {
      this.popoverOpen = false
      return
    }

    this.menuTarget.hidePopover()
    this.popoverOpen = false
  }

  private popoverSupported (): boolean {
    return typeof this.menuTarget.showPopover === 'function' &&
      typeof this.menuTarget.hidePopover === 'function'
  }

  private destroyPopper (): void {
    this.popperInstance?.destroy()
    this.popperInstance = null
  }

  private rowZIndex (): void {
    const tr = this.menuTarget.closest('tr')
    const td = this.menuTarget.closest('td')

    td != null && (td.style.zIndex = this.openValue ? '50' : '')
    tr != null && (tr.style.zIndex = this.openValue ? '50' : '')
  }

  private reset (): void {
    this.closePopover()
    this.destroyPopper()
    this.menuTarget.classList.add('hidden')
  }
}
